import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import Icon from '../components/Icon';
import { CopyButton } from '../screens/create/generatorUi';
import { shortName } from '../product/productUtils';

// The last onboarding step and the "aha": three posts written for the user's
// own customer, in their voice, ready to paste. Free during setup.
const FirstPostsStep = ({ productId, productName, icps }) => {
    const navigate = useNavigate();
    const [posts, setPosts] = useState(null);
    const [isWriting, setIsWriting] = useState(false);
    const [error, setError] = useState('');
    const started = useRef(false);

    const icp = icps.find(item => item.isPrimary) || icps[0];
    const forName = icp ? shortName(icp.name) : 'your customers';

    const write = useCallback(async () => {
        setIsWriting(true);
        setError('');
        try {
            const result = await api.writeFirstPosts(productId, icp?.id);
            setPosts(result.posts);
        } catch (e) {
            if (e.response?.status !== 402) setError("Couldn't write your posts. Please try again.");
        } finally {
            setIsWriting(false);
        }
    }, [productId, icp?.id]);

    useEffect(() => {
        if (started.current) return;
        started.current = true;
        write();
    }, [write]);

    return (
        <>
            <div className="ls-flow-center">
                <div className="ls-flow-column">
                    <div>
                        <span className="ls-eyebrow" style={{ color: 'var(--accent-text)' }}>{productName} is set up</span>
                        <h1 className="ls-flow-h1">
                            {posts ? `Your first 3 posts for ${forName}` : `Writing your first posts for ${forName}…`}
                        </h1>
                        <div className="ls-flow-lead">
                            {icp
                                ? `Written for ${icp.name}, in your brand voice, from everything we just learned. Copy one and post it today.`
                                : 'Written in your brand voice from everything we just learned. Copy one and post it today.'}
                        </div>
                    </div>

                    {error && (
                        <div className="ls-error" role="alert">
                            <Icon name="error" /><span style={{ flex: 1 }}>{error}</span>
                            <button type="button" className="ls-btn ls-btn-secondary" onClick={write}>Try again</button>
                        </div>
                    )}

                    {isWriting && [0, 1, 2].map(i => <div key={i} className="ls-skeleton" style={{ height: 170, borderRadius: 14 }} />)}

                    {posts?.map((post, index) => (
                        <div key={index} className="ls-card" style={{ gap: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                {post.platform && <span className="ls-chip is-medium">{post.platform}</span>}
                                {post.format && <span className="ls-muted" style={{ fontSize: 13 }}>{post.format}</span>}
                                <span style={{ marginLeft: 'auto' }}>
                                    <CopyButton text={[post.hook, post.body, post.hashtags.join(' ')].filter(Boolean).join('\n\n')} />
                                </span>
                            </div>
                            <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.35 }}>{post.hook}</div>
                            <div style={{ fontSize: 15, lineHeight: 1.55, color: 'var(--body)', whiteSpace: 'pre-wrap' }}>{post.body}</div>
                            {post.hashtags.length > 0 && (
                                <div style={{ fontSize: 14, color: 'var(--accent-text)' }}>{post.hashtags.join(' ')}</div>
                            )}
                            {post.why && (
                                <div className="ls-muted" style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                                    <Icon name="lightbulb" size={16} />{post.why}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <footer className="ls-flow-footer">
                <button type="button" className="ls-btn ls-btn-ghost" onClick={() => navigate('/dashboard')}>
                    Go to dashboard
                </button>
                <button
                    type="button"
                    className="ls-btn ls-btn-primary"
                    style={{ fontSize: 15, padding: '12px 24px' }}
                    disabled={isWriting}
                    onClick={() => navigate('/dashboard/plan')}
                >
                    <Icon name="calendar_month" size={18} />Plan my whole week
                </button>
            </footer>
        </>
    );
};

export default FirstPostsStep;
